/* Pro entitlement: a server-set flag at entitlements/{uid}. Clients can read
 * their own doc but NEVER write it (see firestore.rules). Set two ways: by
 * hand (Firebase console, or scripts/grant-pro.mjs) for comps/testers, or by
 * the creemWebhook Cloud Function (functions/index.js, functions/creem.js)
 * when a real Creem checkout completes or a subscription's status changes —
 * see docs/PAYMENTS.md. Either path writes the same doc, so nothing here
 * cares which one set it.
 *
 * The last known answer is cached locally so Pro users get their content
 * instantly (and offline); it re-verifies on every auth change. */
import { doc, getDoc } from "firebase/firestore";
import { db } from "./firebase.js";

const KEY = "towardsgm:pro";
/* Set when a store shell shows the "buy it on the website" hint, read when Pro
 * turns on. Together they answer "of the Android users who were told where to
 * buy it, how many did" without the server having to know which device a
 * purchase came from. Lives here, not in ProModal.jsx, so that file keeps
 * exporting only components (fast refresh). */
export const WEB_HINT_KEY = "towardsgm:pro-web-hint";
const BILLING_KEY = "towardsgm:billing";
let pro = false;
/* Whether this Pro has a Creem customer behind it — i.e. whether there is a
 * billing portal to open. A comp (grant-pro.mjs) is Pro with no customerId,
 * and creemBillingPortal rejects it with failed-precondition, so Settings
 * must not offer "Manage subscription" on the Pro flag alone. */
let billing = false;
try {
  pro = localStorage.getItem(KEY) === "1";
  billing = localStorage.getItem(BILLING_KEY) === "1";
} catch {
  /* default: free */
}
const subs = new Set();

export const isProUser = () => pro;
export const hasBillingAccount = () => billing;

export function subscribePro(fn) {
  subs.add(fn);
  return () => subs.delete(fn);
}

function setPro(next, nextBilling = false) {
  if (next === pro && nextBilling === billing) return;
  pro = next;
  billing = nextBilling;
  try {
    localStorage.setItem(KEY, next ? "1" : "0");
    localStorage.setItem(BILLING_KEY, nextBilling ? "1" : "0");
  } catch {
    /* fine */
  }
  for (const fn of [...subs]) fn(pro);
}

export async function refreshEntitlements(uid) {
  if (!db || !uid) {
    setPro(false);
    return false;
  }
  try {
    const snap = await getDoc(doc(db, "entitlements", uid));
    const data = snap.exists() ? snap.data() : {};
    setPro(Boolean(data.pro), Boolean(data.pro && data.customerId));
  } catch {
    /* offline etc. — keep the cached answer */
  }
  return pro;
}
